import React from 'react';
import { RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

interface RefreshIndicatorProps {
  lastRefresh: Date | null;
  isRefreshing: boolean;
  onRefresh: () => void;
  autoRefreshInterval?: number;
  canRefresh?: boolean; // viewers see the timestamp only
}

const RefreshIndicator: React.FC<RefreshIndicatorProps> = ({
  lastRefresh,
  isRefreshing,
  onRefresh,
  autoRefreshInterval = 60000,
  canRefresh = true
}) => {
  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="flex items-center gap-3">
      <div className="text-sm text-gray-600 dark:text-gray-400">
        {lastRefresh ? (
          <>
            Last updated: <span className="font-medium">{formatTime(lastRefresh)}</span>
          </>
        ) : (
          'Loading...'
        )}
      </div>

      {canRefresh && (
        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Refresh data"
        >
          <motion.div
            animate={{ rotate: isRefreshing ? 360 : 0 }}
            transition={{ duration: 1, repeat: isRefreshing ? Infinity : 0, ease: "linear" }}
          >
            <RefreshCw className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </motion.div>
        </button>
      )}

      {autoRefreshInterval > 0 && (
        <div className="text-xs text-gray-500 dark:text-gray-500">
          Auto-refresh: {autoRefreshInterval / 1000}s
        </div>
      )}
    </div>
  );
};

export default RefreshIndicator;